import { Theme, alpha } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';
import '@mui/x-data-grid/themeAugmentation';
import '@mui/x-date-pickers/themeAugmentation';

import { panelSx } from '@/components/common/Panel/Panel.styles';

import type { ThemeColors } from './colors';

const outlinedInputStyles = (theme: Theme) => ({
  backgroundColor: theme.palette.input.background.main,
  transition: theme.transitions.base,
  '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline':
    {
      border: `1px solid ${theme.palette.input.border.main} !important`,
      borderWidth: '1px !important',
      transition: theme.transitions.base,
    },
  '& .MuiOutlinedInput-input': {
    border: 'none',
    '&::placeholder': {
      color: theme.palette.input.placeholder.main,
      opacity: 1,
    },
    color: theme.palette.text.primary,
  },
  '&.Mui-focused': {
    backgroundColor: theme.palette.input.background.focused,
    '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline':
      {
        borderColor: `${theme.palette.input.border.focused} !important`,
        borderWidth: '1px !important',
        boxShadow: `0 0 15px ${alpha(theme.palette.input.border.focused, 0.25)}`,
      },
  },
  '&:not(.Mui-disabled):not(.Mui-focused):hover': {
    '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline':
      {
        borderColor: `${alpha(theme.palette.input.border.focused, 0.4)} !important`,
      },
  },
  '&.Mui-disabled': {
    '& .MuiOutlinedInput-input, & .MuiPickersOutlinedInput-input': {
      WebkitTextFillColor: theme.palette.text.disabled,
    },
    '&.readOnlyField': {
      backgroundColor: 'transparent',
    },
  },
});

const inputAdornmentStyles = (theme: Theme) => ({
  color: theme.palette.text.primary,

  '& .MuiTypography-root': {
    color: 'inherit',
  },

  '&.Mui-disabled, .Mui-disabled &': {
    color: theme.palette.action.disabled,
    '& .MuiTypography-root': {
      color: 'inherit',
    },
  },
});

/**
 * Component style overrides. Mode-dependent values come from the resolved color
 * set; everything else is derived from the theme so it stays in sync with the
 * palette.
 */
export const createComponents = (
  colors: ThemeColors
): ThemeOptions['components'] => ({
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: colors.appBarBackground,
        border: `1px solid ${colors.border}`,
        backgroundImage: 'none',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: colors.background.paper,
        border: `1px solid ${colors.border}`,
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        // !important is needed to win over MuiPaper's own background, and lives
        // here rather than in the color token so the token stays a plain color.
        backgroundColor: `${colors.menu.background} !important`,
        border: `1px solid ${colors.menu.borderColor}`,
        backdropFilter: 'blur(16px)',
        boxShadow: '0 10px 40px -10px rgba(0,0,0,0.8)',
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        margin: '0 6px',
        marginBottom: '4px',
        '&:last-child': { marginBottom: 0 },
        color: theme.palette.text.primary,
        padding: '10px 16px',
        borderRadius: '6px',
        '&:not(.Mui-selected):hover': {
          backgroundColor: theme.palette.input.background.focused,
          color: colors.menuItemHoverText,
        },
        '&.Mui-selected': {
          backgroundColor: `${alpha(theme.palette.primary.dark, 0.1)} !important`,
          color: theme.palette.primary.main,
          border: `1px solid ${alpha(theme.palette.primary.dark, 0.2)}`,
          fontWeight: 600,
        },
      }),
    },
  },
  MuiLink: {
    styleOverrides: {
      // MUI defaults links to primary.main; primary.dark reads better against
      // both backgrounds. Call sites that pass their own color via sx still win.
      // The underline color is only restated for underline="always", which is
      // the one variant MUI derives from the link color; "hover" underlines use
      // currentColor and need no override.
      root: ({ theme, ownerState }) => ({
        color: theme.palette.primary.dark,
        ...(ownerState.underline === 'always' && {
          textDecorationColor: alpha(theme.palette.primary.dark, 0.4),
        }),
      }),
    },
  },
  MuiSelect: {
    styleOverrides: {
      icon: {
        '.readOnlyField.Mui-disabled &': {
          display: 'none', // Hide dropdown arrow when disabled and read only field
        },
      },
    },
  },
  MuiInput: {
    styleOverrides: {
      root: {
        // Adjust to match OutlinedInput
        paddingLeft: '16px',
        paddingBottom: '8px',
      },
      underline: ({ theme }) => ({
        '&:before, &:after, &:hover:not(.Mui-disabled):before': {
          borderBottom: `1px solid ${theme.palette.input.border.main} !important`,
        },
      }),
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.input.label.main,
        '&.Mui-disabled': {
          color: theme.palette.text.disabled,
        },
      }),
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      positionStart: ({ theme }) => inputAdornmentStyles(theme),
      positionEnd: ({ theme }) => inputAdornmentStyles(theme),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => outlinedInputStyles(theme),
    },
  },
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => outlinedInputStyles(theme),
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiTouchRipple-root': { display: 'none' },
        '& .MuiTabs-list': {
          borderBottom: `1px solid ${theme.palette.input.background.focused}`,
        },
        '& .MuiTab-root': {
          transition: `${theme.transitions.base}, font-weight 0s`,
          color: theme.palette.ghostWhite.main,
          '&:not(.Mui-selected):hover': {
            background: theme.palette.input.background.main,
            color: theme.palette.text.primary,
          },
          '&.Mui-selected': {
            color: theme.palette.primary.main,
            fontWeight: 700,
            background: `linear-gradient(to top, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 100%)`,
          },
        },
        '& .MuiTabs-indicator': {
          background: theme.palette.primary.main,
          boxShadow: `0 -2px 12px ${alpha(theme.palette.primary.main, 0.5)}`,
          borderTopLeftRadius: '2px',
          borderTopRightRadius: '2px',
        },
      }),
    },
  },
  MuiDialog: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiDialog-backdrop': {
          backgroundColor: colors.dialogBackdrop,

          backdropFilter: 'blur(8px)',
        },
        '& .MuiDialog-container': {
          '& > .MuiPaper-root': {
            ...panelSx(theme),
          },
        },
      }),
      paper: {
        backgroundImage: 'none',
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: colors.action.hover,
        },
        '&.Mui-selected': {
          backgroundColor: colors.action.selected,
          '&:hover': {
            backgroundColor: colors.action.selected,
          },
        },
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      option: {
        '&.Mui-focused:not(:hover)': {
          backgroundColor: 'transparent',
        },
      },
    },
  },
  // DataGrid styling
  MuiDataGrid: {
    styleOverrides: {
      root: {
        '--DataGrid-t-color-border-base': `${colors.border} !important`,
        '& .MuiDataGrid-columnHeaders': {
          borderBottom: '1px solid',
        },
        '& .MuiDataGrid-columnHeader': {
          fontSize: '0.875rem',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: '700 !important',
          },
        },
      },
    },
  },
  // Global scrollbar styling
  MuiCssBaseline: {
    styleOverrides: {
      'body, html, ul, div:not(.MuiDataGrid-root):not(.MuiDataGrid-root *)': {
        '&::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        /* Scrollbar hidden by default */
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'transparent',
          borderRadius: '3px',
          transition: 'background-color 0.2s ease',
        },
        /* Scrollbar shown on hover */
        '&:hover::-webkit-scrollbar-thumb': {
          backgroundColor: colors.scrollbarThumb,
        },

        /* Firefox support */
        scrollbarWidth: 'thin',
        scrollbarColor: 'transparent transparent',

        '&:hover': {
          scrollbarColor: `${colors.scrollbarThumb} transparent`,
        },
      },
      // Autofill styling
      'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active':
        {
          WebkitBoxShadow: `0 0 0 30px ${colors.autofill.background} inset !important`,
          WebkitTextFillColor: colors.text.primary,
          borderRadius: '4px',
          transition: 'background-color 5000s ease-in-out 0s',
        },
      // Fix for MUI TextField border interruption when autofilled
      'input:-webkit-autofill ~ .MuiOutlinedInput-notchedOutline': {
        borderColor: `${colors.autofill.border} !important`,
      },
      'input:-webkit-autofill ~ .MuiOutlinedInput-notchedOutline legend': {
        width: 'auto !important',
        maxWidth: 'calc(100% - 2px)',
      },
    },
  },
});
